import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Sparkles, Crown, MessageCircle, UserPlus, X } from 'lucide-react';

/**
 * LiveNotificationWidget — Admin-only bottom-right notification popup.
 *
 * Polls GET /api/admin/live-activity every 5s and surfaces each new platform
 * event as a 4-second toast with a 2-second glow pulse. Colors:
 *   • Top-up            → Green  (#10b981)
 *   • Beta invited      → Blue   (#3b82f6)
 *   • Lifetime upgrade  → Gold   (#f59e0b)
 *   • Feedback          → Purple (#a855f7)
 */

const POLL_INTERVAL_MS = 5000;
const VISIBLE_MS = 4000;       // toast on-screen duration
const PULSE_MS = 2000;          // glow pulse duration (first 2s of visibility)
const MAX_STACK = 4;            // max simultaneous toasts on screen

const PALETTE = {
  topup:             { color: '#10b981', icon: Wallet,        label: 'Top-up',         shadow: 'rgba(16,185,129,0.55)' },
  first_topup:       { color: '#10b981', icon: Sparkles,      label: 'First Top-up!',  shadow: 'rgba(16,185,129,0.65)' },
  beta_invited:      { color: '#3b82f6', icon: UserPlus,      label: 'Beta Invited',   shadow: 'rgba(59,130,246,0.55)' },
  beta_accepted:     { color: '#3b82f6', icon: UserPlus,      label: 'Beta Accepted',  shadow: 'rgba(59,130,246,0.55)' },
  workspace_created: { color: '#3b82f6', icon: UserPlus,      label: 'New Workspace',  shadow: 'rgba(59,130,246,0.55)' },
  lifetime_upgrade:  { color: '#f59e0b', icon: Crown,         label: 'LIFETIME',       shadow: 'rgba(245,158,11,0.60)' },
  feedback_submitted:{ color: '#a855f7', icon: MessageCircle, label: 'Feedback',       shadow: 'rgba(168,85,247,0.55)' },
};

function pickPalette(ev) {
  // Treat topup with is_first=true as first_topup
  if (ev.type === 'topup' && ev.is_first) return PALETTE.first_topup;
  return PALETTE[ev.type] || PALETTE.topup;
}

function Toast({ event, onDismiss }) {
  const pal = pickPalette(event);
  const Icon = pal.icon;
  const isFirstTopup = event.type === 'topup' && event.is_first;

  useEffect(() => {
    const t = setTimeout(onDismiss, VISIBLE_MS);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 80, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.92, transition: { duration: 0.25 } }}
      transition={{ type: 'spring', damping: 22, stiffness: 280 }}
      data-testid={`live-notification-${event.type}`}
      className="relative w-[340px] rounded-2xl border overflow-hidden cursor-pointer"
      style={{
        background: 'linear-gradient(135deg, rgba(18,20,26,0.96) 0%, rgba(10,11,15,0.96) 100%)',
        borderColor: `${pal.color}44`,
        backdropFilter: 'blur(16px)',
      }}
      onClick={onDismiss}
    >
      {/* Pulsing glow — fires once for first 2s */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.85, 0] }}
        transition={{ duration: PULSE_MS / 1000, times: [0, 0.5, 1], ease: 'easeOut' }}
        className="pointer-events-none absolute -inset-1 rounded-2xl"
        style={{
          boxShadow: `0 0 32px 6px ${pal.shadow}`,
          background: `radial-gradient(circle at 20% 50%, ${pal.color}25, transparent 60%)`,
        }}
      />

      {/* Left color bar */}
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ background: `linear-gradient(180deg, ${pal.color}, ${pal.color}66)` }}
      />

      <div className="relative flex items-start gap-3 p-3.5 pl-4">
        {/* Icon badge */}
        <div
          className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{
            background: `${pal.color}1f`,
            boxShadow: `inset 0 0 0 1px ${pal.color}55`,
          }}
        >
          <Icon size={18} style={{ color: pal.color }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span
              className="text-[10px] uppercase tracking-[0.18em] font-extrabold"
              style={{ color: pal.color }}
            >
              {pal.label}
            </span>
            {isFirstTopup && (
              <span
                className="text-[9px] uppercase tracking-[0.18em] font-extrabold px-1.5 py-0.5 rounded"
                style={{ background: `${pal.color}22`, color: pal.color }}
              >
                🏆 First
              </span>
            )}
          </div>
          <p className="text-[13px] text-white leading-snug line-clamp-2">
            {event.msg || `${pal.label} event`}
          </p>
          {event.workspace_name && (
            <p className="text-[11px] text-slate-400 mt-1 truncate">
              {event.workspace_name}
            </p>
          )}
        </div>

        {/* Close */}
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="flex-shrink-0 p-1 rounded text-slate-500 hover:text-white hover:bg-white/5"
          aria-label="Dismiss"
          data-testid="live-notification-dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {/* Auto-dismiss progress bar */}
      <motion.div
        aria-hidden
        initial={{ width: '100%' }}
        animate={{ width: '0%' }}
        transition={{ duration: VISIBLE_MS / 1000, ease: 'linear' }}
        className="absolute bottom-0 left-0 h-[2px]"
        style={{ background: pal.color, boxShadow: `0 0 8px ${pal.color}` }}
      />
    </motion.div>
  );
}

export default function LiveNotificationWidget() {
  const { api, user, isAdmin } = useAuth();
  const location = useLocation();
  const [toasts, setToasts] = useState([]);
  const sinceRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  // Only appear inside the authenticated app shell. Public marketing pages
  // (landing, /pricing, /features, /compare, /demo) and all auth flows never
  // surface admin live-activity toasts — even if the admin is logged in.
  const isAppRoute = /^\/(admin|workspace|dashboard|billing|profile|settings)(\/|$)/i.test(
    location.pathname || ''
  );
  const skipOnPath = !isAppRoute || /^\/(login|signup|set-password|beta-onboarding|billing-setup|auth\/callback)/i.test(
    location.pathname || ''
  );

  const enqueue = useCallback((events) => {
    if (!events?.length) return;
    setToasts((curr) => {
      const next = [...curr];
      for (const ev of events) {
        // Dedupe by time+type+workspace_id — activity rows don't have a stable id
        const key = `${ev.time}|${ev.type}|${ev.workspace_id || ''}|${ev.user_id || ''}`;
        if (seenIdsRef.current.has(key)) continue;
        seenIdsRef.current.add(key);
        next.push({ _key: key, ...ev });
      }
      // Cap stack size — drop oldest
      return next.slice(-MAX_STACK);
    });
  }, []);

  useEffect(() => {
    if (!user || !isAdmin || skipOnPath) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const params = sinceRef.current ? { since: sinceRef.current } : {};
        const res = await api.get('/api/admin/live-activity', { params });
        if (cancelled) return;
        const { events = [], server_time } = res.data || {};
        if (events.length > 0) {
          enqueue(events);
          sinceRef.current = events[events.length - 1].time;
        } else if (!sinceRef.current && server_time) {
          sinceRef.current = server_time;
        }
      } catch { /* silent — don't spam admins with error toasts */ }
    };

    poll();
    const iv = setInterval(poll, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, isAdmin, skipOnPath]);

  const dismiss = useCallback((key) => {
    setToasts((curr) => curr.filter((t) => t._key !== key));
  }, []);

  if (!user || !isAdmin || skipOnPath) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[140] flex flex-col-reverse gap-3 pointer-events-none"
      data-testid="live-notification-widget"
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <div key={t._key} className="pointer-events-auto">
            <Toast event={t} onDismiss={() => dismiss(t._key)} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}
